import { useState } from 'react';
import { Box } from '@mui/material';
import {
  CasButton,
  CasChip,
  CasRenderArrayAsRows,
  CasTable,
  CasErrorDisplay,
} from 'src/compass/shared/components';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import Iconify from 'src/components/iconify';
import { IGeneralObjectType } from 'src/compass/site/util';
import {
  CustomerComposeDialog,
  CasRiskManagerSelect,
  CasTagMultiSelect,
  CasYesNoSelect,
  useSiteProperties,
} from 'src/compass/application';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';

import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import customerSearchFilter from './_internal/customerSearchFilter';
import CustomerDialog from './CustomerDialog';
import CustomerBatchSendDialog from './CustomerBatchSendDialog';
import { sendMessageColumn } from './RenderSendMessage';
import { RenderRiskManagerName } from './RenderRiskManagerName';
import { RenderLastTouchpoint } from './RenderLastTouchpoint';
import { useTouchpointFrequencyOptions } from './TouchpointFrequencySelect';
import { TOUCHPOINT_ENTITY } from './TouchpointsTab';

export default function CustomersTab() {
  const [dialogRowId, setDialogRowId] = useState<number | null>(null);
  const [reloadTable, setReloadTable] = useState<Date | null>(null);
  const [composeEntityId, setComposeEntityId] = useState<number | null>(null);
  const batchSendDialog = useCasBoolean();
  // The listing's own url + filters, so Batch Send mails exactly the rows these filters match.
  const [requestMetadata, setRequestMetadata] = useState<IGeneralObjectType>();

  const {
    data: riskManagerData,
    isLoading,
    isError,
    error,
  } = useRetrieve({ url: 'CurrentUser/RiskManager' });

  // Send and Batch Send stay hidden until the Show Customer Send feature flag is on
  // (Administration > Configuration > Feature Flags), so they can stay off in Prod until ready.
  const { featureFlags, isLoading: isSitePropertiesLoading } = useSiteProperties();
  const showSend = !!featureFlags.showCustomerSend;

  // Frequency lookup: { id: Days, name: Description }. Used in the
  // Last Touchpoint cell to surface the Description for entities that have
  // no last-touchpoint date but a "0 Days" (excluded) frequency.
  const { options: freqOptions } = useTouchpointFrequencyOptions({});

  // Waits on the flag too: CasTable reads its columns once, at mount, so a Send column the flag
  // turned on afterwards would never appear.
  if (isLoading || isSitePropertiesLoading) return <CasLoader />;
  if (isError) return <CasErrorDisplay message={error.message} />;

  return (
    <>
      <CasTable
        entity={TOUCHPOINT_ENTITY}
        setRequestMetadata={setRequestMetadata}
        includeExport={false}
        refreshBtn
        update={reloadTable}
        columns={[
          {
            field: 'name',
            render: (props: ICasRenderCellProps) => (
              <RenderRiskManagerName {...props} onClick={setDialogRowId} />
            ),
          },
          {
            field: 'contacts',
            sortable: false,
            render: CasRenderArrayAsRows,
          },
          {
            field: 'tags',
            sortable: false,
            render: ({ row }: ICasRenderCellProps) =>
              (row.tags as string[] | undefined)?.map((tag, index) => (
                <Box key={`${index}${tag}`} sx={{ mb: 0.25 }}>
                  <CasChip label={tag} size="small" color="lightBlue" />
                </Box>
              )),
          },
          {
            field: 'lastTouchpointDate',
            label: 'Last Touchpoint',
            render: (props: ICasRenderCellProps) => (
              <RenderLastTouchpoint {...props} freqOptions={freqOptions} />
            ),
          },
          ...(showSend ? [sendMessageColumn(setComposeEntityId)] : []),
        ]}
        filtersInDrawer
        /*
          All filters live in the Filters drawer. `isPrimaryFilter` ones also stay
          on the toolbar for quick access (they share the drawer's value, so either
          copy stays in sync, and they get no chip since they're already visible).
          Tags is a multi-select matching ANY selected tag.
        */
        /* CasTable appends the full-screen toggle AFTER whatever it is given, which is what puts
           Batch Send immediately to its left. */
        toolbarButtons={
          showSend
            ? [
                <CasButton
                  key="batchSend"
                  startIcon={<Iconify icon="ic:outline-forum" />}
                  onClick={batchSendDialog.setTrue}
                >
                  Batch Send
                </CasButton>,
              ]
            : []
        }
        filters={[
          {
            type: 'component',
            name: 'employeeId',
            label: 'RMC',
            isPrimaryFilter: true,
            initialValue: riskManagerData?.riskManagerId,
            // Reset restores the current user rather than clearing: the API rejects a
            // null employeeId for BusDev users.
            resetValue: riskManagerData?.riskManagerId,
            component: CasRiskManagerSelect,
            // All = every customer regardless of RMC, so it is main-office only; a branch
            // user is scoped to their own people, and BusDev to themselves. The API applies
            // the same rule (EntityTouchpointService.GetList) rather than trusting this.
            includeAllOption: !!riskManagerData?.isMainOffice && !riskManagerData?.isBusDev,
          },
          {
            ...customerSearchFilter,
            isPrimaryFilter: true,
            autoFocus: true,
          },
          {
            type: 'component',
            name: 'tagIds',
            label: 'Tags',
            component: CasTagMultiSelect,
          },
          {
            type: 'component',
            name: 'overdueOnly',
            label: 'Touchpoint Overdue',
            includeAllOption: true,
            component: CasYesNoSelect,
          },
          {
            type: 'component',
            name: 'lrpHistory',
            label: 'LRP History?',
            includeAllOption: true,
            component: CasYesNoSelect,
          },
          {
            type: 'component',
            name: 'cmeHistory',
            label: 'CME History?',
            includeAllOption: true,
            component: CasYesNoSelect,
          },
          {
            type: 'number',
            name: 'lrpQuoteWithinDays',
            label: 'LRP Quote Click Within (Days)',
          },
          {
            name: 'phone',
            label: 'Phone',
          },
        ]}
      />
      {batchSendDialog.value && requestMetadata && (
        <CustomerBatchSendDialog
          onClose={batchSendDialog.setFalse}
          requestMetadata={requestMetadata}
        />
      )}
      {composeEntityId && (
        <CustomerComposeDialog
          entityId={composeEntityId}
          onClose={() => setComposeEntityId(null)}
        />
      )}
      {dialogRowId && (
        <CustomerDialog
          id={dialogRowId}
          onClose={(dataUpdated?: boolean) => {
            setDialogRowId(null);
            if (dataUpdated) setReloadTable(new Date());
          }}
        />
      )}
    </>
  );
}
